// Phase E1's meta content (decision 0060): the achievements, the codex's entries and rules, the tutorial prompts and
// photo mode's numbers, read from their tables (content/tables/achievements.json, codex.json, codex-rules.json,
// tutorials.json, photo.json; the words in meta-strings.json) into @rpg/meta's definitions. Lore pages are placed on
// the layout as points of interest are (src/hud/content.ts's `resolveAt`).
import type { AchievementDef, CodexEntryDef, CodexRule, Match, PhotoOptions, TutorialDef } from "@rpg/meta";
import type { World } from "@rpg/world";
import type { ContentTables, SpikeContent } from "../content";
import { resolveAt } from "../hud/content";

/** A lore page lying in the world. */
export interface LorePage {
  id: string;
  x: number;
  z: number;
  reach: number;
}

/** A pose photo mode offers. */
export interface PhotoPose {
  id: string;
  pose: string;
  at: number;
  label: string;
}

/** Everything the meta layer reads. */
export interface MetaContent {
  achievements: AchievementDef[];
  achievementRows: ContentTables["achievements"][];
  codex: CodexEntryDef[];
  codexRows: ContentTables["codex"][];
  rules: CodexRule[];
  tutorials: TutorialDef[];
  tutorialRows: ContentTables["tutorials"][];
  pages: LorePage[];
  photo: { options: PhotoOptions; profile: string; poses: PhotoPose[]; maxBlur: number; file: string };
  /** Each quest's kind (main or side), for the records. */
  questKinds: Map<string, string>;
  /** The glyph collection's size (the tuning's rpg.glyph.maxGlyphs). */
  maxGlyphs: number;
}

const match = (m: Record<string, unknown> | undefined): Match | undefined => (m ? (m as Match) : undefined);

/** Read the meta content; `world` places the lore pages (null: none placed, for checks without a world). */
export function readMetaContent(content: SpikeContent, world: World | null, photoId = "valley"): MetaContent {
  const reg = content.registry;
  const achievementRows = [...reg.rows("achievements")];
  const achievements: AchievementDef[] = achievementRows.map((r) => ({
    id: r.id,
    ...(r.counter ? { counter: { on: r.counter.on, ...(r.counter.match ? { match: match(r.counter.match)! } : {}), ...(r.counter.distinct ? { distinct: r.counter.distinct } : {}), ...(r.counter.add ? { add: r.counter.add } : {}) } } : {}),
    ...(r.target !== undefined ? { target: r.target } : {}),
    ...(r.when ? { when: r.when } : {}),
    ...(r.hidden ? { hidden: true } : {}),
  }));
  const codexRows = [...reg.rows("codex")];
  const codex = codexRows.map((r) => ({ id: r.id, section: r.section }));
  const rules: CodexRule[] = reg.rows("codex-rules").map((r) => ({ on: r.on, entry: r.entry, mark: r.mark, ...(r.match ? { match: match(r.match)! } : {}) }));
  const tutorialRows = [...reg.rows("tutorials")];
  const tutorials: TutorialDef[] = tutorialRows.map((r) => ({
    id: r.id,
    verb: r.verb.id,
    ...(r.when ? { when: r.when } : {}),
    ...(r.after ? { after: r.after } : {}),
    ...(r.delay !== undefined ? { delay: r.delay } : {}),
    ...(r.usedWhen ? { usedWhen: r.usedWhen } : {}),
  }));
  const pages: LorePage[] = [];
  if (world)
    for (const r of codexRows) {
      if (!r.page) continue;
      const [x, z] = resolveAt(world, r.id, r.page.at as ContentTables["pois"]["at"]);
      pages.push({ id: r.id, x: x + (r.page.offset?.[0] ?? 0), z: z + (r.page.offset?.[1] ?? 0), reach: r.page.reach });
    }
  const p = reg.get("photo", photoId);
  if (!p) throw new Error(`meta: no photo row "${photoId}"`);
  const pair = (v: number[]): readonly [number, number] => [v[0]!, v[1]!];
  const photo = {
    options: { radius: p.radius, minHeight: p.minHeight, speed: p.speed, fov: pair(p.fov), pitch: pair(p.pitch), aperture: pair(p.aperture) },
    profile: p.profile.id,
    poses: p.poses.map((x) => ({ id: x.id, pose: x.pose.id, at: x.at, label: x.label.id })),
    maxBlur: p.maxBlur,
    file: p.file,
  };
  const questKinds = new Map(reg.rows("quests").map((q) => [q.id, String((q as { kind?: string }).kind ?? "side")]));
  const maxGlyphs = reg.get("tuning", "rpg.glyph.maxGlyphs")?.value ?? 6;
  return { achievements, achievementRows, codex, codexRows, rules, tutorials, tutorialRows, pages, photo, questKinds, maxGlyphs };
}
